import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { combineLatest, forkJoin } from "rxjs";

import { AdaaHelper } from "../../../../core/utils";
import {
  ConfirmationModalComponent,
  DataTableComponent,
  FormActionButtonsComponent,
  FormDropdownComponent,
  FormInputComponent,
} from "../../../../shared/components";
import { Constants } from "../../../../shared/constants/constants";
import { AdaaBoolean, Language, PageMode } from "../../../../shared/constants/enums";
import {
  ForecastedValuesMetric,
  ForecastedValuesMetricResponse,
  TableActionEvent,
  ValueText,
} from "../../../../shared/models";
import {
  ForecastedValuesApiService,
  LanguageService,
  PropertiesService,
  SystemLayoutService,
} from "../../../../shared/services";

@Component({
  selector: "adaa-forecasted-values-metrics-editor",
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    DataTableComponent,
    FormInputComponent,
    FormDropdownComponent,
    FormActionButtonsComponent,
  ],
  templateUrl: "./forecasted-values-metrics-editor.html",
  styleUrl: "./forecasted-values-metrics-editor.scss",
})
export class ForecastedValuesMetricsEditorComponent implements OnInit {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _router = inject(Router);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _forecastedValuesApiService = inject(ForecastedValuesApiService);
  private readonly _propertiesService = inject(PropertiesService);
  private readonly _modalService = inject(NgbModal);
  private readonly _languageService = inject(LanguageService);
  private readonly _systemLayoutService = inject(SystemLayoutService);

  id = input.required<string>();
  pageMode = input.required<string>();

  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();
  metricForm: FormGroup;
  AdaaHelper = AdaaHelper;
  Constants = Constants;
  months: ValueText[] = [];
  years: ValueText[] = [];
  currentYear = new Date().getFullYear();
  metricValues = signal<ForecastedValuesMetricResponse[]>([]);
  editingValueId = signal<number | undefined>(undefined);

  isViewMode = computed(() => this.pageMode() === PageMode.view);

  public get isArabic(): boolean {
    return AdaaHelper.getCurrentLang() === Language.Arabic;
  }

  public get displayLabel(): string {
    const nameEN = this.metricForm.get("nameEN")?.value;
    const nameAE = this.metricForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public ngOnInit(): void {
    this._prepareForm();
    this._initPage();
    this._initDdls();

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => this.goBack(),
    });

    this._systemLayoutService.hasCycleChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => this.goBack(),
    });
  }

  public addMetricValue(): void {
    const actual = this.metricForm.get("actual")?.value as number;
    const month = this.metricForm.get("month")?.value;
    const year = this.metricForm.get("year")?.value;

    if (!actual) {
      this._toastrService.error(this._translateService.instant("data_entry.value_empty"));
      return;
    }

    if (this.metricValues().some((e) => e.month === month && e.year === year)) {
      this._toastrService.error(this._translateService.instant("data_entry.cannotAddMessage"));
      return;
    }

    this._addMetricValue();
  }

  public updateMetricValue(): void {
    if (!this.editingValueId()) return;

    this._forecastedValuesApiService
      .updateMetricValue(this.editingValueId()!, this.metricForm.get("actual")?.value)
      .subscribe({
        next: (response) => {
          if (response.inError) return;

          this._toastrService.success(this._translateService.instant("notification.success.save"));
          this._getTableData();
          this.cancelUpdate();
        },
      });
  }

  public cancelUpdate(): void {
    this.editingValueId.set(undefined);
    this.metricForm.get("year")?.enable();
    this.metricForm.get("month")?.enable();
  }

  public actionClicked(event: TableActionEvent): void {
    if (event.event === "delete") {
      this.cancelUpdate();
      this._deleteMetricValue(event.data.id);
    } else if (event.event === "edit") {
      this.editingValueId.set(event.data.id);
      this.metricForm.get("year")?.disable();
      this.metricForm.get("month")?.disable();
      this.metricForm.get("actual")?.setValue(event.data.value);
      this.metricForm.get("month")?.setValue(event.data.month);
      this.metricForm.get("year")?.setValue(event.data.year);
    }
  }

  public goBack(): void {
    this._router.navigateByUrl("console/forecasted-values/metrics");
  }

  private _prepareForm(): void {
    this.metricForm = this._formBuilder.group({
      id: [{ value: null, disabled: true }],
      nameAE: [{ value: null, disabled: true }],
      nameEN: [{ value: null, disabled: true }],
      dscAE: [{ value: null, disabled: true }],
      dscEN: [{ value: null, disabled: true }],
      dataSourceAE: [{ value: null, disabled: true }],
      dataSourceEN: [{ value: null, disabled: true }],
      dataType: [{ value: null, disabled: true }],
      dataTypeAE: [{ value: null, disabled: true }],
      dataTypeEN: [{ value: null, disabled: true }],
      type: [{ value: null, disabled: true }],
      typeName: [{ value: null, disabled: true }],
      month: [{ value: 1, disabled: false }],
      year: [{ value: this.currentYear, disabled: false }],
      actual: [{ value: null, disabled: false }],
      periodDate: [{ value: new Date(this.currentYear, 0, 1).getTime(), disabled: false }],
    });

    const month = this.metricForm.get("month")?.valueChanges;
    const year = this.metricForm.get("year")?.valueChanges;

    if (!month || !year) return;

    combineLatest([month, year])
      .pipe(this._untilDestroyed())
      .subscribe(([month, year]) => {
        const periodDate = new Date(`${year}-${month}`).getTime();
        this.metricForm.get("periodDate")?.setValue(periodDate);
      });
  }

  private _initPage(): void {
    forkJoin([
      this._forecastedValuesApiService.getMetricById(+this.id()),
      this._forecastedValuesApiService.getMetricValues(+this.id()),
      this._propertiesService.getPropById(Constants.CONSTANT_MEASUREMENT_UNIT_METRICS),
    ]).subscribe({
      next: ([metric, metricValues, measurementUnits]) => {
        if (metric.inError) {
          this._toastrService.error(this._translateService.instant("notification.error.unknown_object"));
          this.goBack();
          return;
        }

        this.metricForm.patchValue(metric.responseData);
        this.metricForm.get("typeName")?.patchValue(this._getTypeName(metric.responseData.type));

        const measurementUnit = measurementUnits.responseData?.find((e) => e.id === metric.responseData.dataType);
        if (measurementUnit) {
          this.metricForm.get("dataTypeAE")?.patchValue(measurementUnit.nameAE);
          this.metricForm.get("dataTypeEN")?.patchValue(measurementUnit.nameEN);
        }

        this.metricValues.set(metricValues.responseData ?? []);
      },
    });
  }

  private _initDdls(): void {
    for (let i = 1; i <= 12; i++) {
      this.months.push({ value: i, text: i.toString() });
    }

    for (let i = 2000; i <= this.currentYear; i++) {
      this.years.push({ value: i, text: i.toString() });
    }
  }

  private _getTypeName(type?: string): string {
    switch (type) {
      case Constants.METRICS_TYPE.GLOBAL:
        return this._translateService.instant("main_types.G");
      case Constants.METRICS_TYPE.LOCAL:
        return this._translateService.instant("main_types.L");
      case Constants.METRICS_TYPE.DIMENSION:
        return this._translateService.instant("main_types.D");
      default:
        return "";
    }
  }

  private _addMetricValue(): void {
    this._forecastedValuesApiService.addMetricValue(this._getModel()).subscribe({
      next: (response) => {
        if (response.inError) return;

        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this._getTableData();
      },
    });
  }

  private _deleteMetricValue(id: number): void {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.header = "data_entry.metric_value_delete_yes_no_title";
    modal.componentInstance.title = "data_entry.metric_value_delete_yes_no_information";

    modal.result.then((e) => {
      if (e) {
        this._deleteMetricValueConfirmed(id);
      }
    });
  }

  private _deleteMetricValueConfirmed(id: number): void {
    this._forecastedValuesApiService.deleteMetricValue(id).subscribe({
      next: (response) => {
        if (response.inError) return;

        this._toastrService.success(this._translateService.instant("notification.success.remove"));
        this._getTableData();
      },
    });
  }

  private _getModel(): ForecastedValuesMetric {
    const actual = this.metricForm.get("actual")?.value;

    const model: ForecastedValuesMetric = {
      dataStatus: AdaaBoolean.N,
      metricId: +this.id(),
      metricType: this.metricForm.get("type")?.value ?? "",
      month: this.metricForm.get("month")?.value ?? 0,
      periodDate: this.metricForm.get("periodDate")?.value ?? 0,
      updated: false,
      value: actual ? +actual : 0,
      year: this.metricForm.get("year")?.value ?? 0,
    };

    return model;
  }

  private _getTableData(): void {
    this._forecastedValuesApiService.getMetricValues(+this.id()).subscribe({
      next: (response) => {
        if (response.inError) return;

        this.metricValues.set(response.responseData ?? []);
      },
    });
  }
}
